import React from 'react';
import { IDataRow } from 'react-bootstrap-table-next';
import {
  IStandardDocumentVersionData,
  getPrettyPublisherType,
} from '../../types/order/documents/shared/interfaces';
import {
  BodyText,
  Button,
  Grid,
  IconSize,
  IconType,
  NavigationItem,
  RotatingIcon,
  SmallBodyText,
} from '../../ui-toolkit';
import { DocumentCategoriesByName } from '../../types/order/documents/IDocumentCategoryType';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { DangerText } from './styledComponents/DangerText';
import { createDownloadDocumentUrl } from 'API/order/documents/adminDocumentsAPI';
import { downloadFile } from '../../ui-toolkit/utilities/downloadFile';
import { DateFormat, getDateString } from '../../ui-toolkit/utilities/getDateString';

const buildCategoryFormatter = (documentCategoriesByName: DocumentCategoriesByName) => {
  return (
    _cell: IDataRow<IStandardDocumentVersionData>[keyof IDataRow<IStandardDocumentVersionData>],
    row?: IDataRow<IStandardDocumentVersionData>
  ) => {
    if (!row) return null;
    const category = documentCategoriesByName[row.category];
    if (!category) return null;

    return (
      <div>
        <NavigationItem
          appearance={'minimal'}
          intent={'primary'}
          text={category.label}
          isInline
          onClick={() => downloadFile(createDownloadDocumentUrl(row.id, row.versionId), row.fileName)}
        />
        {category.isRepeatable && <SmallBodyText>{row.fileName}</SmallBodyText>}
      </div>
    );
  };
};

const buildUploaderTypeFormatter = (isZillowClosingEnabled: boolean) => (
  _cell: IDataRow<IStandardDocumentVersionData>[keyof IDataRow<IStandardDocumentVersionData>],
  row?: IDataRow<IStandardDocumentVersionData>
) => {
  if (!row) return null;
  return getPrettyPublisherType(row.uploaderType, isZillowClosingEnabled);
};

const buildUpdatedAtFormatter =
  (
    fetching: boolean,
    deleteDocumentId: string | null,
    onDeleteDocumentClick: (document: IStandardDocumentVersionData) => Promise<void>
  ) =>
  (
    _cell: IDataRow<IStandardDocumentVersionData>[keyof IDataRow<IStandardDocumentVersionData>],
    row?: IDataRow<IStandardDocumentVersionData>
  ) => {
    if (!row) return null;
    return (
      <Grid templateColumns={'1fr min-content'} textAlign={'right'} alignItems={'center'}>
        <BodyText>{getDateString(DateFormat.long, row.updatedAt, true)}</BodyText>
        {row.uploaderType === 'VENDOR_USER' && (
          <>
            {fetching && row.id === deleteDocumentId ? (
              <RotatingIcon size={IconSize.Small} type={IconType.Loading} />
            ) : (
              <Button isInline intent={'ghost'} onClick={() => onDeleteDocumentClick(row)}>
                <DangerText>
                  <FontAwesomeIcon icon={faTrashAlt} />
                </DangerText>
              </Button>
            )}
          </>
        )}
      </Grid>
    );
  };

export const DocumentBlockFormatters = (
  documentCategoriesByName: DocumentCategoriesByName,
  fetching: boolean,
  deleteDocumentId: string | null,
  onDeleteDocumentClick: (document: IStandardDocumentVersionData) => Promise<void>,
  isZillowClosingEnabled: boolean,
) => ({
  categoryFormatter: buildCategoryFormatter(documentCategoriesByName),
  uploaderTypeFormatter: buildUploaderTypeFormatter(isZillowClosingEnabled),
  updatedAtFormatter: buildUpdatedAtFormatter(fetching, deleteDocumentId, onDeleteDocumentClick),
});
